/**
 * The editable quotation: its lines and its totals, as one unit.
 *
 * Rendered by `/quote` and embedded in `/revisions` below the requests list, so a price edited
 * on one screen and a price edited on the other cannot drift apart — there is one component and
 * one set of hooks behind both.
 *
 * `structural` is what differs between them. `/quote` omits it and gets today's behaviour
 * exactly: prices are editable, quantities are not. `/revisions` passes it and gets qty and
 * remove controls, which queue into a batch rather than saving — because those go through
 * `apply_revisions`, which bumps the revision, and one round of corrections should be one
 * revision. See `lib/revisionBatch.ts`.
 *
 * `allowSend` is the OTHER thing that differs, and for a sharper reason (fix round, final
 * review: C1). `/revisions` has its own re-issue button below this component, gated on
 * `open.length === 0` — "resolve every open request first" — and on the queued batch having
 * NOTHING to do with whether `TotalsPanel`'s send card may fire: that card's own gate
 * (`!blocked && !sending && status === 'draft' && recipients.length > 0`) knows nothing about
 * open requests or an unapplied batch. Two send buttons on one screen, one of them missing the
 * guard the page was built around, is a way to email a customer a quotation with unresolved
 * requests and un-applied edits still sitting in the batch — irreversible, since it is a real
 * email. So the HOST decides: `/quote` omits `allowSend` and gets today's card exactly; the
 * default here is `true` only so a caller that forgets to think about it fails safe for `/quote`,
 * the screen that has always had exactly one send button. `/revisions` passes `false`, which
 * does not merely disable the button — `TotalsPanel` does not render the send section at all, so
 * there is nothing there to look live and be clicked by mistake.
 */

import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../components/Toast'
import { useCachedAttachmentLoader } from '../../lib/api/attachmentBlob'
import { downloadFile } from '../../lib/api/fileDownload'
import { fetchQuotationPdf, quotationPdfName, quotationPdfUrl } from '../../lib/api/quotationPdf'
import { Button } from '../../components/ui'
import { useItemsByCode } from '../../lib/api/useItems'
import { usePricingRules } from '../../lib/api/useSettings'
import {
  useApplyQuotationCurrency,
  useSendQuotation,
  useMarkQuotationSent,
  useUpdateQuotationLine,
  useUpdateQuotationLineFields,
  useUpdateQuotationTerms,
  type QuotationTerms,
} from '../../lib/api/useQuotation'
import { checkLines, effectiveLines, previewTotals } from '../../lib/pricing'
import { marginBlockers, describeMarginBlockers, describeUnpriced } from '../../lib/marginGate'
import { QuotationPreviewModal } from './QuotationPreviewModal'
import { CommissionPanel } from './CommissionPanel'
import { TaxesCard } from './TaxesCard'
import { TermsCard } from './TermsCard'
import { PaymentTermsCard } from './PaymentTermsCard'
import { QuotationHeaderCard, type QuotationHeaderPatch } from './QuotationHeaderCard'
import { useUpdateEnquiry, type UpdateEnquiryPatch } from '../../lib/api/useEnquiryMutations'
import { DerivedFiguresSync } from './DerivedFiguresSync'
import { taxesWritePatch, type DerivedFigures } from '../../lib/derivedFigures'
import { completionWrite } from '../../lib/quotationCompletion'
import { totalQuantity } from '../../lib/quotationTotals'
import { applyMarkup } from '../../lib/quotationCommission'
import { runSerially } from '../../lib/api/serialWrites'
import { convert, FX_FALLBACK } from '../../lib/fx'
import { useCurrentUser } from '../../lib/api/useMasters'
import { useQuotationExcel } from '../../lib/api/quotationExcel'
import { useExchangeRates } from '../../lib/api/useMasters'
import { useCustomerRecipients } from '../../lib/api/useCustomerContacts'
import { describeRate, isStale, pickRate, toWireFx } from '../../lib/exchangeRates'
import { QuoteLinesTable } from './QuoteLinesTable'
import { quotationSendNote } from '../../lib/quotationSendNote'
import { leadingDiscount } from '../../lib/additionalDiscount'
import { baseCurrencyFor } from '../../lib/api/baseCurrency'
import { TotalsPanel } from './TotalsPanel'
import type { Currency, Enquiry, Money, Quotation, RuleViolation } from '../../lib/types'

/** Queued edits, keyed by item_code, for the row to show its pending state. */
export interface StructuralEditing {
  pending: Map<string, { op: 'set_qty' | 'remove' | 'add'; qty: number }>
  onQueueQty: (itemCode: string, qty: number) => void
  onQueueRemove: (itemCode: string) => void
  onDiscard: (itemCode: string) => void
  /**
   * A line's PRICE was changed on the quotation.
   *
   * `update_quotation_line` bumps nothing — a reprice is not an `apply_revisions` op and has none
   * — so a round of pure rate edits produced no revision at all and nothing to issue. The page
   * records that the round is dirty and bumps ONCE when it is applied; a bump per line would turn
   * one round of customer corrections into five revisions, which is the very thing the batch
   * exists to prevent.
   */
  onRepriced?: () => void
}

export interface QuotationEditorProps {
  enquiryId: string
  quotation: Quotation
  enquiry: Enquiry
  /** Absent on `/quote` — no qty/remove controls, which is today's behaviour exactly. */
  structural?: StructuralEditing
  /**
   * Whether `TotalsPanel`'s own send-to-customer card may render at all. Defaults to `true` —
   * `/quote` never passes this and keeps its one send button; `/revisions` passes `false` because
   * it has its own re-issue button with guards this component cannot see (open requests, the
   * queued batch). See this file's own header for why disabling was not enough.
   */
  /** The send button's wording. Defaults to a first send; `/revisions` words it as a re-issue. */
  sendLabel?: string
  /**
   * A host-imposed reason sending is blocked, or `null`. The editor cannot know these: on
   * `/revisions` an unresolved customer request must be dealt with first, and that gate used to
   * live on a SECOND send button further down the page — which is how a purchaser could bypass
   * it entirely (final review, C1). One button, gated by whoever owns the screen.
   */
  sendBlockedReason?: string | null
  /**
   * Whether markup / commission / convenience may be edited. Defaults to "only while draft",
   * which is `/quote`'s rule; `/revisions` passes `true` because changing a sent quotation is
   * what that screen is for.
   */
  termsEditable?: boolean
  /** The page is read-only (`lib/readOnly.ts`) — no automatic write may run either. */
  locked?: boolean
}

export function QuotationEditor({
  enquiryId: id,
  quotation,
  enquiry,
  structural,
  sendLabel = 'Send quotation to customer',
  sendBlockedReason = null,
  termsEditable,
  locked = false,
}: QuotationEditorProps) {
  const navigate = useNavigate()
  const toast = useToast()

  // `null` until the user chooses; the quotation's own `grouped` is the default.
  const [groupedOverride, setGroupedOverride] = useState<boolean | null>(null)
  /*
    `null` until the user picks, so the default can follow the QUOTATION rather than a constant.
    This used to initialise to the literal `'AED'`, which was simply wrong for a customer priced
    in anything else — the screen opened claiming a currency the document was not written in.
  */
  const [currencyOverride, setCurrencyOverride] = useState<Currency | null>(null)

  const rulesQuery = usePricingRules()
  const ratesQuery = useExchangeRates()

  // IDENTITY for the lookup — never `customer_name` (fix round, task-11-report.md: I4).
  const customerId = quotation.customer

  /*
    Who this actually goes to. Resolved HERE rather than left to `send_quotation`, whose own
    lookup is broken two ways (ask `B8`) — see `useCustomerContacts.ts`. An empty result is a
    real answer: 1561 customers on this bench have no contact at all, and sending to nobody is
    what produced the `Unknown column` error people were seeing.
  */
  const recipientsQuery = useCustomerRecipients(customerId || null)
  const recipients = recipientsQuery.data ?? []

  const itemCodes = useMemo(() => quotation.lines.map((l) => l.item_code), [quotation])
  const { byCode: itemsByCode } = useItemsByCode(itemCodes)

  // Live rule feedback: the same rules the server re-runs on send, applied as the user types.
  const violations: RuleViolation[] = useMemo(() => {
    if (!rulesQuery.data) return []
    return checkLines(quotation.lines, rulesQuery.data)
  }, [quotation, rulesQuery.data])

  const violationsByLine = useMemo(() => {
    const map = new Map<string, RuleViolation[]>()
    for (const violation of violations) {
      const existing = map.get(violation.line_id)
      if (existing) existing.push(violation)
      else map.set(violation.line_id, [violation])
    }
    return map
  }, [violations])

  const updateLine = useUpdateQuotationLine(id, quotation.id, {
    onError: (error) => toast(`Could not update the line: ${error.message}`),
    // `/revisions` needs to know the round is dirty even when no qty/add/remove was queued.
    onSuccess: () => structural?.onRepriced?.(),
  })
  // The printed texts — description and offer remark (item 8).
  const updateLineFields = useUpdateQuotationLineFields(id, quotation.id, {
    onError: (error) => toast(`Could not update the line: ${error.message}`),
    onSuccess: () => structural?.onRepriced?.(),
  })


  /*
    The customer workbook. Both `/quote` and `/revisions` get it from here — one control, one
    implementation, and the download is the only way today to see what the customer would receive
    (`B9`: the quotation email attaches nothing).

    Fetched with the Clerk bearer and saved as a blob (`fileDownload.ts`, 2026-09-21): a new tab
    carries no bearer, and the deployed proxy answers it 401.
  */
  const currentUser = useCurrentUser()
  const loadBlob = useCachedAttachmentLoader()
  const excel = useQuotationExcel({
    onReady: (fileUrl, fileName) => void downloadFile(fileUrl, fileName, loadBlob).catch((error: unknown) => toast(error instanceof Error ? error.message : String(error))),
    onError: (message) => toast(message),
  })

  // The PDF, as the server renders it (`quotationPdf.ts`). Same toast on failure as the workbook.
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const downloadPdf = () => {
    setDownloadingPdf(true)
    fetchQuotationPdf(quotation.id)
      .then((blob) => downloadFile(quotationPdfUrl(quotation.id), quotationPdfName(quotation.id), async () => blob))
      .catch((error: unknown) => toast(error instanceof Error ? error.message : String(error)))
      .finally(() => setDownloadingPdf(false))
  }

  const updateTerms = useUpdateQuotationTerms(id, quotation.id, {
    onError: (error) => toast(`Could not update the quotation: ${error.message}`),
  })
  /*
    The ENQUIRY follows the quotation's customer, sales person and purchaser (Akhil, 2026-09-17).
    A duplicated requirement is created for the source's customer and then re-addressed on this
    card — and the context bar above reads the enquiry, so it kept naming the old customer after
    the quotation had a new one. The two documents must not disagree about who the order is for;
    the RFQ header card syncs the other way for the same reason (`syncQuotation`).
  */
  const updateEnquiry = useUpdateEnquiry(id, { onError: (error) => toast(`Quotation updated, but the enquiry could not follow: ${error.message}`) })
  const updateEnquiryMutate = updateEnquiry.mutate
  // Stable, because the header card's follow-on effects (sales person, address fill) list it.
  const commitHeader = useCallback(
    (patch: QuotationHeaderPatch) => {
      updateTerms.mutate(patch)
      const follow: UpdateEnquiryPatch = {
        ...(patch.customer !== undefined ? { customer: patch.customer } : {}),
        ...(patch.sales_person !== undefined ? { sales_person: patch.sales_person } : {}),
      }
      if (Object.keys(follow).length > 0) updateEnquiryMutate(follow)
    },
    [updateTerms.mutate, updateEnquiryMutate],
  )
  const writeDerived = useCallback((figures: DerivedFigures) => updateTerms.mutate(figures), [updateTerms.mutate])

  /*
    Applying the viewed currency to the DOCUMENT — a separate, confirmed act, never a side effect
    of choosing a view. `pickRate` is the same rule the quotation was priced with, so the screen
    and the re-price cannot disagree; no rate means no offer to apply, rather than an offer that
    fails when pressed.
  */
  const applyCurrency = useApplyQuotationCurrency(id, quotation.id, {
    onSuccess: () => toast(`Quotation re-priced in ${displayCurrency}`),
    onError: (error: { message: string }) => toast(`Could not re-price: ${error.message}`),
  })

  /*
    "Mailed it to the customer yourself?" — the sales team sends some quotations from Outlook
    (Akhil, 2026-09-18). Confirmed in words that say what changes: the enquiry moves to Quotation
    Sent and the RFQ builder locks, and NO mail goes out.
  */
  const markSent = useMarkQuotationSent(id, quotation.id, {
    onSuccess: () => toast('Marked as sent — the enquiry is now at Quotation Sent. No mail was sent from here.'),
    onError: (error: { message: string }) => toast(`Could not mark as sent: ${error.message}`),
  })
  const confirmMarkSent = () => {
    if (
      !window.confirm(
        `Mark ${quotation.id} as sent WITHOUT emailing it from here?\n\n` +
          'Use this only if the quotation was already mailed to the customer separately (Outlook, WhatsApp, in person).\n\n' +
          'The enquiry moves to Quotation Sent, the RFQ builder locks, and any further change becomes a revision.',
      )
    )
      return
    markSent.mutate({ by: currentUser.data?.email ?? null, currentStatus: enquiry.status })
  }

  const send = useSendQuotation(id, quotation.id, {
    onSuccess: () => toast('Quotation emailed to customer'),
    onError: (error) =>
      toast(
        error.status === 409
          // `violations[]` is the authoritative gate (Task 11) — every reason lands in
          // `error.messages`, not just a generic "blocked".
          ? `Blocked: ${error.messages.join('; ') || 'pricing-rule violations must be fixed first.'}`
          : `Could not send: ${error.message}`,
      ),
  })

  /*
    The rates the screen converts with.

    `quotation.fx` is ALWAYS `{}` on this wire — `get_quotation` carries no snapshot — so relying
    on it meant `convert()` found no rate for any pair and returned every figure untouched. The
    currency picker moved the "Total (…)" label and not one number on the page. The real
    `Currency Exchange` table stands in, through the same `pickRate` rule the quotation was
    priced with, so the screen and the document cannot disagree.
  */
  const fx = ratesQuery.data && ratesQuery.data.length > 0 ? toWireFx(ratesQuery.data) : (quotation.fx ?? FX_FALLBACK)

  // The currency the quotation is WRITTEN in — what the picker starts on.
  const quotationCurrency = quotation.totals.total.currency
  const displayCurrency = currencyOverride ?? quotationCurrency

  /*
    What the picker offers: the quotation's own currency FIRST — it must always be selectable, or
    the screen cannot show the document as written — then everything we hold a rate from it to.

    Nothing is offered that we could not convert to, because an option that silently leaves every
    figure unchanged is worse than an absent one.
  */
  const currencies = (() => {
    const reachable = (ratesQuery.data ?? [])
      .filter((row) => row.from_currency?.trim().toUpperCase() === quotationCurrency)
      .map((row) => row.to_currency?.trim().toUpperCase() as Currency)
    return [quotationCurrency, ...[...new Set(reachable)].filter((c) => c && c !== quotationCurrency).sort()]
  })()

  /*
    The rate behind whatever the picker is showing, and its date.

    Only when something is actually being converted: on the quotation's own currency there is no
    rate involved and saying so would be noise. `missing` is its own case rather than silence —
    the figures on screen are then NOT in the currency the picker claims, and that has to be
    visible rather than inferred from numbers that failed to move.
  */
  /*
    The rate behind the CURRENT view, kept as its own value because two things need it: the note
    under the picker, and the offer to re-price the document into that currency. Deriving it twice
    would let the number shown differ from the number applied.
  */
  const currencyRate =
    displayCurrency === quotationCurrency
      ? null
      : (pickRate(ratesQuery.data ?? [], quotationCurrency, displayCurrency)?.rate ?? null)
  // The COMPANY's currency — what `Quotation.exchange_rate` is measured from. See `RepriceRates`.
  const baseCurrency = baseCurrencyFor(enquiry.header.company) as Currency

  const rateNote = (() => {
    if (displayCurrency === quotationCurrency) return null
    const picked = pickRate(ratesQuery.data ?? [], quotationCurrency, displayCurrency)
    if (picked === null) {
      return {
        text: `No ${quotationCurrency} → ${displayCurrency} rate on file — figures below are still ${quotationCurrency}.`,
        stale: false,
        missing: true,
      }
    }
    const now = new Date()
    return {
      text: `Shown in ${displayCurrency} at ${picked.rate} — ${describeRate(picked, now)}.`,
      stale: isStale(picked.date, now),
      missing: false,
    }
  })()

  /*
    A queued removal or qty change (`/revisions` only) sits on screen — via `structural.pending`
    — for as long as it takes the purchaser to review the whole batch, not just a network round
    trip. The row above already shows it struck through or re-numbered; a total that still
    counted a "removed" line's full price the whole time would be a number the purchaser is
    about to invalidate by clicking Apply. Dropped/resized here for the PREVIEW only — the
    batch itself (`lib/revisionBatch.ts`) is what actually reaches the server.
  */
  const previewLines = useMemo(
    () => effectiveLines(quotation.lines, structural?.pending),
    [quotation.lines, structural],
  )

  /**
   * While a line edit is in flight the server's totals are one step behind, so the panel
   * shows a client-side preview of the pending state (§3: the recompute is FE). The moment
   * the PATCH lands, `quotation.totals` — the number that actually ships — takes over.
   *
   * Converts through `fx` above, NOT `quotation.fx`. The typed price arrives in whatever the
   * picker is showing and has to be read back into the line's own currency; against the empty
   * snapshot that conversion was a no-op, so a price typed while viewing SAR was previewed as
   * though it had been typed in AED.
   */
  const pendingPatch = updateLine.isPending ? updateLine.variables : undefined
  const hasStructuralPending = !!structural && structural.pending.size > 0
  const totals =
    pendingPatch || hasStructuralPending
      ? previewTotals(
          previewLines.map((l) =>
            pendingPatch && l.line_id === pendingPatch.lineId
              ? { ...l, sell_unit: convert(pendingPatch.patch.sell_unit, l.sell_unit.currency, fx) }
              : l,
          ),
          {
            commission_pct: quotation.totals.commission_pct,
            convenience: quotation.totals.convenience,
            markup_pct: quotation.totals.markup_pct,
          },
        )
      : quotation.totals

  const grouped = groupedOverride ?? quotation.grouped
  const enforce = rulesQuery.data?.enforce ?? false
  const blocked = violations.length > 0 && enforce

  /*
    Point 25 — the unconditional margin gate. Computed on `previewLines`, the same list the totals
    preview uses, so a queued removal of the one offending line unblocks the send immediately
    instead of after the batch is applied.

    Combined with, not replacing, whatever the caller already passed: `/revisions` blocks the send
    on open revision requests, and a quotation can perfectly well have both problems. The caller's
    reason is shown first because it is the more structural of the two.
  */
  /** Point 30 — the customer's-eye view. Read-only; opening it changes nothing. */
  const [previewing, setPreviewing] = useState(false)

  /*
    Price locks. SESSION-ONLY: `Quotation Line` has no lock field (`D24`), so these do not survive
    a reload — which is stated on screen rather than left to be discovered when a markup wipes a
    price somebody thought was protected.

    Held here rather than in the table so the commission panel can count them and say how many a
    markup will skip.
  */
  const [lockedLineIds, setLockedLineIds] = useState<ReadonlySet<string>>(() => new Set())
  const toggleLock = (lineId: string) =>
    setLockedLineIds((current) => {
      const next = new Set(current)
      if (next.has(lineId)) next.delete(lineId)
      else next.add(lineId)
      return next
    })
  const allLocked = quotation.lines.length > 0 && quotation.lines.every((l) => lockedLineIds.has(l.line_id))

  /*
    The discount, in the shape the engine takes — built ONCE and handed to both the taxes grid and
    the totals panel, so the two cannot compute different grand totals from the same document.

    The percentage leads when there is one: it is the figure the purchaser actually set, and the
    amount is the server's rounding of it.
  */
  // The stored percentage leads — same rule the commission panel applies (`leadingDiscount`).
  const discountLead = leadingDiscount(quotation.discount)
  const discountInput = discountLead ? { applyOn: quotation.discount.apply_on, ...discountLead } : null

  const marginReason = useMemo(() => describeMarginBlockers(marginBlockers(previewLines)), [previewLines])
  const effectiveSendBlockedReason = sendBlockedReason ?? marginReason
  /*
    Unpriced lines WARN, they do not block (Akhil, 2026-09-07). Kept apart from
    `effectiveSendBlockedReason` on purpose: one refuses the send, this one lets it through and
    says what is going out at 0.00, so a to-be-advised line is a decision rather than an accident.
  */
  const unpricedWarning = useMemo(() => describeUnpriced(previewLines), [previewLines])

  return (
    <>
      {/*
        The commission panel. Above the table because what it does lands ON the table — a markup
        applied from below the lines it reprices reads as an afterthought.
      */}
      <CommissionPanel
        quotation={quotation}
        displayCurrency={displayCurrency}
        fx={fx}
        /*
          Two separate signals, deliberately.

          `saving` DIMS. Every field in the panel commits on blur, so committing one would disable
          the others mid-interaction — clicking from Management into Software landed on an input the
          browser had just disabled, and nothing could be typed. `readOnly` is the real gate: a sent
          quotation's terms cannot be edited at all.
        */
        saving={updateTerms.isPending || updateLine.isPending}
        readOnly={!(termsEditable ?? quotation.status === 'draft')}
        onCommitTerms={(patch) => updateTerms.mutate(patch as never)}
        lockedCount={lockedLineIds.size}
        onApplyMarkup={(pct, commissionPct) => {
          /*
            One PATCH per line that actually moved, run STRICTLY IN SEQUENCE.

            Per line rather than one wholesale write because `update_quotation` replaces `items`
            entirely, and rebuilding every row here would risk dropping any field this screen does
            not model.

            In sequence because Frappe rejects a write whose read predates the document's current
            `modified`. Fired together — which is what a bare `for` loop over `.mutate()` does —
            the first landed and every other one failed with "has been modified after you have
            opened it", four milliseconds apart. See `lib/api/serialWrites.ts`.
          */
          const repriced = applyMarkup(quotation.lines, pct, lockedLineIds, commissionPct)
          const writes = repriced
            .filter((line) => {
              const before = quotation.lines.find((l) => l.line_id === line.line_id)
              return before && before.sell_unit.amount !== line.sell_unit.amount
            })
            .map((line) => () =>
              // `cost`/`fx` come from the CALLER by design — see `UpdateLineVars`, where reading
              // them out of a query cache once corrupted every margin written through Revisions.
              updateLine.mutateAsync({
                lineId: line.line_id,
                cost: line.cost_unit,
                sellCurrency: line.sell_unit.currency,
                fx,
                patch: { sell_unit: line.sell_unit },
              }),
            )

          if (writes.length === 0) {
            toast('Every line is already at that price.')
            return
          }

          void runSerially(writes).then(({ done, firstError }) => {
            if (firstError) {
              // Says what DID land — "it failed" on a partially applied bulk edit leaves the
              // purchaser unable to tell which lines to check.
              toast(`Repriced ${done} of ${writes.length} lines, then stopped: ${firstError.message}`)
              return
            }
            /*
              The toast said "at 0% over cost" for lines that landed 11.1% over it (Akhil,
              2026-09-17): the markup is 0, but a 10% commission grossed into the price makes
              cost ÷ 0.9. Say what was applied AND what it comes to, or the number on the row
              contradicts the message under it.
            */
            const overCost = commissionPct > 0 ? ((1 + pct / 100) / (1 - commissionPct / 100) - 1) * 100 : pct
            toast(
              commissionPct > 0
                ? `Repriced ${done} line${done === 1 ? '' : 's'}: ${pct}% markup + ${commissionPct}% commission grossed into the price = ${overCost.toFixed(1)}% over cost.`
                : `Repriced ${done} line${done === 1 ? '' : 's'} at ${pct}% over cost.`,
            )
          })
        }}
      />

      {/*
        Taxes and charges. Below the commission panel and above the lines, because it reads off the
        net the lines produce and feeds the grand total below them — the order on screen follows the
        order of the arithmetic.
      */}
      <TaxesCard
        rows={quotation.taxes}
        netTotal={totals.sell_subtotal.amount}
        totalQty={totalQuantity(previewLines)}
        discount={discountInput}
        // The document's currency, not the view's — the grid's figures are what a save writes,
        // and "133 AED" labelled "$133" is how the currency picker used to read here.
        currency={quotationCurrency}
        viewCurrency={displayCurrency}
        fx={fx}
        saving={updateTerms.isPending}
        // Wholesale — every row we intend to keep — with each row's COMPUTED amount and running
        // total, 1-based `row_id`s (what a "Previous Row" charge resolves against), and the
        // totals the rows produce. See `taxesWritePatch`.
        onSave={(rows) => updateTerms.mutate(taxesWritePatch(quotation, rows))}
        // The quotation's own company; a document from before it was mandatory falls back to the enquiry's.
        company={quotation.company || enquiry.header.company}
      />

      {/* Terms go to the customer, so they sit with the other document-level cards rather than
          being buried below the lines. */}
      <TermsCard
        tcName={quotation.tc_name}
        terms={quotation.terms}
        saving={updateTerms.isPending}
        onCommit={(patch) => updateTerms.mutate(patch as never)}
      />
      {/* Its own card, beside T&C — one pick, nothing to edit; see the component. */}
      <PaymentTermsCard
        value={quotation.payment_terms_template}
        saving={updateTerms.isPending}
        onCommit={(payment_terms_template) => updateTerms.mutate({ payment_terms_template } as never)}
      />
      {/* The discount value, PO cost and tax amounts the server does not compute (2026-09-16). */}
      <DerivedFiguresSync
        quotation={quotation}
        enabled={!locked && !updateTerms.isPending && !updateLine.isPending}
        write={writeDerived}
      />
      {/* Customer, address, reference, sales person, purchaser, logistics, currency — one
          collapsible card (Akhil, 2026-09-16), in place of the Bill-to and Vessel cards. */}
      <QuotationHeaderCard
        quotation={quotation}
        company={enquiry.header.company}
        enquiryType={enquiry.header.enquiry_type}
        saving={updateTerms.isPending || updateEnquiry.isPending}
        onCommit={commitHeader}
        // The one write that completes an older quotation's header (2026-09-19) — quotation and
        // enquiry both, so each single-field write after it can go.
        onCommitAll={(drafts) => {
          const { quotation: q, enquiry: e } = completionWrite(
            {
              company: quotation.company,
              customer: quotation.customer,
              vessel: quotation.vessel,
              port: quotation.port,
              delivery_location: quotation.delivery_location,
              sales_person: quotation.sales_person,
              purchaser: quotation.purchaser,
              customer_reference_no: quotation.customer_reference_no,
              currency: quotation.currency,
            },
            { company: enquiry.header.company, enquiryType: enquiry.header.enquiry_type },
            drafts,
          )
          updateTerms.mutate(q as QuotationTerms, { onSuccess: () => toast('Quotation details saved — every field can now be changed on its own.') })
          if (Object.keys(e).length > 0) updateEnquiryMutate(e)
        }}
        // Both documents: the enquiry's Select, and the quotation's own Link (mandatory since
        // 2026-09-17 pm) — they must not disagree.
        onCommitEnquiryType={(enquiry_type) => {
          updateEnquiry.mutate({ enquiry_type })
          updateTerms.mutate({ enquiry_type })
        }}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 14, marginBottom: 8 }}>
        {/* Lock every line at once, and the way back. The label states what it will DO, not what
            the current state is — a button called "All locked" leaves you guessing what pressing
            it does. */}
        <span style={{ fontSize: 12.5, color: 'var(--muted)', marginRight: 'auto' }}>
          🔒 {lockedLineIds.size} of {quotation.lines.length} line(s) locked
          <span style={{ opacity: 0.8 }}> · editing a price locks its line · session only</span>
        </span>
        <Button
          size="sm"
          onClick={() =>
            setLockedLineIds(allLocked ? new Set() : new Set(quotation.lines.map((l) => l.line_id)))
          }
        >
          {allLocked ? 'Unlock all' : 'Lock all prices'}
        </Button>
        {/* Point 30. Placed with the other view controls rather than beside Send: it is a thing you
            do WHILE reviewing, often several times, not a step in sending. */}
        <Button size="sm" onClick={() => setPreviewing(true)}>
          👁 Client view
        </Button>
        <label style={{ fontSize: 12.5, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={grouped} onChange={(e) => setGroupedOverride(e.target.checked)} /> Group items
        </label>
      </div>

      {previewing ? (
        <QuotationPreviewModal quotation={quotation} enquiry={enquiry} onClose={() => setPreviewing(false)} />
      ) : null}

      {violations.length > 0 ? (
        <div
          className="banner"
          style={{ background: 'var(--dangerbg)', borderColor: 'var(--dangerline)' }}
          role="alert"
        >
          ⛔ <b>Pricing-rule violation</b> on the highlighted line(s).{' '}
          {enforce ? 'Sending is blocked until fixed.' : 'Warnings only (enforcement off).'}{' '}
          <span className="linkish" onClick={() => navigate('/settings')}>
            Edit rules
          </span>
        </div>
      ) : null}

      <div className="split">
        <QuoteLinesTable
          lines={quotation.lines}
          grouped={grouped}
          itemsByCode={itemsByCode}
          violationsByLine={violationsByLine}
          rules={rulesQuery.data}
          displayCurrency={displayCurrency}
          fx={fx}
          margins={rulesQuery.data?.category_margins}
          busyLineId={updateLine.isPending ? updateLine.variables?.lineId : undefined}
          structural={structural}
          lockedLineIds={lockedLineIds}
          onToggleLock={toggleLock}
          onChangeText={(lineId, patch) => updateLineFields.mutate({ lineId, patch })}
          onChangeSellUnit={(lineId, sell: Money) => {
            // The line's own cost — never a cache lookup (fix round, task-11-report.md: C1).
            const line = quotation.lines.find((l) => l.line_id === lineId)
            if (!line) return
            /*
              A hand-typed price LOCKS ITS OWN LINE.

              Typing a price is the clearest possible statement that this line is decided — and it
              is exactly the price a later "Apply to every line" would overwrite without asking.
              Requiring the purchaser to also remember the checkbox makes the lock protect only the
              lines they thought to protect in advance, which is not the ones that need it.

              Locking never blocks further typing (`QuoteRow`'s own note), so this cannot trap
              anyone: they can keep editing, and unlock deliberately if they want the markup back.
            */
            setLockedLineIds((current) => (current.has(lineId) ? current : new Set(current).add(lineId)))
            // `sellCurrency` is the quotation's, not the cost's — they differ whenever the
            // customer is billed in something other than the company's currency.
            updateLine.mutate({
              lineId,
              cost: line.cost_unit,
              sellCurrency: line.sell_unit.currency,
              fx,
              patch: { sell_unit: sell },
            })
          }}
        />

        <TotalsPanel
          totals={totals}
          displayCurrency={displayCurrency}
          commissionPct={quotation.commission.line_commission ?? 0}
          marginNote={
            quotation.commission.line_commission && quotation.commission.line_commission > 0 && quotation.commission.markup > 0
              ? `${quotation.commission.markup}% markup + ${quotation.commission.line_commission}% commission, grossed up into the price = ${(((1 + quotation.commission.markup / 100) / (1 - quotation.commission.line_commission / 100) - 1) * 100).toFixed(1)}% over cost — the same in every currency`
              : null
          }
          companyCurrency={baseCurrency}
          exchangeRate={quotation.exchange_rate}
          fx={fx}
          blocked={blocked}
          sending={send.isPending}
          status={quotation.status}
          currencies={currencies}
          rateNote={rateNote}
          onChangeCurrency={setCurrencyOverride}
          recipients={recipients}
          recipientsPending={recipientsQuery.isPending}
          /*
            The logged-in user travels as `sender` so the customer sees a person rather than the
            mail account. The server ignores it today (measured — see `SendQuotationInput.sender`);
            it is sent so that the day `send_quotation` grows the parameter its own RFQ sibling
            already has, nothing here needs changing.
          */
          onSend={() =>
            send.mutate({
              to: recipients,
              // The purchaser on CC, always (Akhil, 2026-09-17).
              cc: enquiry.header.purchaser ? [enquiry.header.purchaser] : [],
              sender: currentUser.data?.full_name,
              // The email body — carries the payment terms to the customer. See `quotationSendNote`.
              note: quotationSendNote(quotation),
            })
          }
          quotationCurrency={quotationCurrency}
          applyingCurrency={applyCurrency.isPending}
          onApplyCurrency={
            displayCurrency === quotationCurrency || currencyRate === null
              ? null
              : () => {
                  // Confirmed, because it rewrites every price a customer was quoted. A native
                  // confirm is enough friction for an action reached from a picker.
                  const ok = window.confirm(
                    `Re-price this quotation from ${quotationCurrency} to ${displayCurrency} at ${currencyRate}?\n\n` +
                      `Every selling price changes, and so does the customer's total.`,
                  )
                  if (ok) {
                    // `step` converts the lines; `companyToTarget` is what `exchange_rate` means.
                    const companyToTarget =
                      displayCurrency === baseCurrency ? 1 : (pickRate(ratesQuery.data ?? [], baseCurrency, displayCurrency)?.rate ?? null)
                    if (companyToTarget === null) {
                      toast(`No exchange rate on file for ${baseCurrency} → ${displayCurrency}; cannot re-price.`)
                      return
                    }
                    applyCurrency.mutate({ currency: displayCurrency, rates: { step: currencyRate, companyToTarget } })
                  }
                }
          }
          unpricedWarning={unpricedWarning}
          onDownload={() => excel.mutate(quotation.id)}
          downloading={excel.isPending}
          onDownloadPdf={downloadPdf}
          downloadingPdf={downloadingPdf}
          // The margin gate applies to the workbook as it does to the send (2026-09-19).
          downloadBlockedReason={marginReason}
          onMarkSent={locked ? undefined : confirmMarkSent}
          markingSent={markSent.isPending}
          sendLabel={sendLabel}
          sendBlockedReason={effectiveSendBlockedReason}
          lines={previewLines}
          taxes={quotation.taxes}
          discount={discountInput}
        />
      </div>
    </>
  )
}
